import { useContext, useMemo } from 'react';

import {
  EARLY_CHECKIN,
  LATE_CHECKOUT,
} from 'app/components/commons/Experience/Availability/utils';
import { isBillingInformationComplete } from 'app/components/pages/Extranet/commons/BillingInformation/utils';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useHotelFinance } from 'app/hooks/data/core/extranet/hotel/useHotelFinance';
import type { Bed } from 'app/typings';
import {
  BABY_COT_BED_CATEGORY_ID,
  DAY_PACKAGE_ROOM_CATEGORY_ID,
  GUEST_BED_BED_CATEGORY_ID,
} from 'app/typings';
import { ChannelManager } from 'app/typings/channelManagers';
import { ExtranetPackStatus } from 'app/typings/core/extranet/hotel/packs';

import { createTaskLists, sortTasks } from '../Modern/Utils';

export const useModernTaskCenter = () => {
  const { hotelInfo } = useContext(HotelContext);

  const { experiencesList, packsList, roomsList } =
    useContext(ModernHotelContext);

  const hasChannelManager = !!(
    hotelInfo?.channelManager &&
    hotelInfo?.channelManager !== ChannelManager.NONE
  );

  const publishedPacks = packsList.filter(
    (pack) => pack.status === ExtranetPackStatus.PUBLISHED
  );

  const isMoment = publishedPacks.some((pack) => pack.isDaytime);

  const filteredRooms = roomsList.filter(
    (room) => room.categoryId !== DAY_PACKAGE_ROOM_CATEGORY_ID
  );

  const packageCount = publishedPacks.length;

  const hasBabyCot = useMemo(
    () =>
      filteredRooms.length > 0 &&
      filteredRooms.some((room) =>
        room.beds.some(
          (bed: Bed) => bed.categoryId === BABY_COT_BED_CATEGORY_ID
        )
      ),
    [filteredRooms]
  );

  const hasGuestBed = useMemo(
    () =>
      filteredRooms.length > 0 &&
      filteredRooms.some((room) =>
        room.beds.some(
          (bed: Bed) => bed.categoryId === GUEST_BED_BED_CATEGORY_ID
        )
      ),
    [filteredRooms]
  );

  const hasEarlyCheckInExp = useMemo(
    () => experiencesList.some((exp) => exp.categoryId === EARLY_CHECKIN),
    [experiencesList]
  );

  const hasLateCheckOutExp = useMemo(
    () => experiencesList.some((exp) => exp.categoryId === LATE_CHECKOUT),
    [experiencesList]
  );

  const { data: finance } = useHotelFinance(hotelInfo?.id);
  const hasBillingInformation = isBillingInformationComplete(finance);

  const { doneTasks, missingTasks } = createTaskLists(
    packageCount,
    hasBabyCot,
    hasGuestBed,
    hasChannelManager,
    !!hotelInfo?.lastMinuteNightRoomDiscountRate,
    hasEarlyCheckInExp,
    hasLateCheckOutExp,
    hasBillingInformation,
    isMoment
  );

  return sortTasks(doneTasks, missingTasks);
};
